/**
 * ADM-08 课程商品编辑对话框（course-product-catalog:SC-01~SC-21 前端）。
 *
 * 宽屏双栏：左侧配置表单（省/市级联、基础信息、封面上传、SKU 课时配置、定价分期、卖点标签），
 * 右侧「家长端实时预览」卡随表单即时刷新（UI 原型方案 ADM-08）。
 * - 省/市级联：切换省份清空城市与 SKU 配置；未选城市前商品名称与 SKU 选择禁用，SKU 打包配置区显示「请先设置城市」提示（选后消失）
 * - SKU 配置行（O-14 判别联合）：每行先选售卖形态（课时包/期限卡）——
 *   课时行含商品内课时数（选 SKU 后预填其课时数默认值，design D14）；
 *   期限行含期限天数（档位快捷 月/季/半年/年卡 30/90/180/365 天，可手改显示「自定义」）
 *   与卡面标价（按「SKU 课时标价 ÷ 课时数默认值 × 期限天数」一天一次课估算预填，
 *   档位/天数/SKU 变化时未手工定价才联动重算；元转分整数安全）。
 *   同一商品内一个 SKU 至多一行（单一形态），重复添加/双形态字段互斥前端拦截。
 * - 原价合计（系统计算只读）= Σ行标价：课时行取 SKU 课时标价、期限行取卡面标价
 * - 封面上传：凭证签发 -> directUpload 真：cos-js-sdk-v5 临时密钥直传；假：直接采用 objectKey（design D6）
 * - 定价（元 ×100 转分）、首付款比例 0~100%（0~1 闭区间）、尾款周期 >= 1 天；尾款比例自动计算只读
 * - 底部：暂存 / 上架（二次确认后先保存再 publish）/ 取消
 */
import { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Divider, Input, InputNumber, Popconfirm, Select, Space, Tag, Typography, Upload } from 'antd';
import { message } from '../../utils/antd-message';
import { DraggableModal } from '../../components/DraggableModal';
import { DeleteOutlined, PlusOutlined, UploadOutlined } from '@ant-design/icons';
import {
  productApi,
  type ProductItem,
  type SkuChargeMode,
} from '../../api/products.api';
import type { CourseSkuItem } from '../../api/course-skus.api';
import { chargeModeLabel, toSelectOptions } from '../../api/labels';
import { useCampusOptions } from '../../hooks/useCampusOptions';
import { uploadObjectToCos } from '../../utils/cos-upload';

const { Text, Title } = Typography;

/** 期限卡 UI 快捷档位（月/季/半年/年卡仅为天数快捷项，不进领域枚举；支持自定义天数）。 */
const PERIOD_PRESET_OPTIONS = [
  { days: 30, label: '月卡' },
  { days: 90, label: '季卡' },
  { days: 180, label: '半年卡' },
  { days: 365, label: '年卡' },
] as const;

/** 省/市级行政区划节点（一期无地区主数据，静态字典）。 */
export interface RegionNode {
  /** GB/T 2260 六位行政区划代码（提交 provinceId/cityId 用代码，家长端按代码过滤地区） */
  code: string;
  name: string;
  cities: Array<{ code: string; name: string }>;
}

/**
 * 省/市静态级联字典（design D2；Id 为 GB/T 2260 六位代码，名称仅展示）。
 * 口径与家长端小程序 `pages/products/index.vue` 的 REGION_CODE 一致，两端各自维护
 * （miniapp 隔离约束禁止跨端共享页面级字典，contracts 为生成目录禁手改）。
 */
export const REGION_TREE: RegionNode[] = [
  { code: '110000', name: '北京市', cities: [{ code: '110100', name: '北京市' }] },
  {
    code: '420000',
    name: '湖北省',
    cities: [
      { code: '422800', name: '恩施州' },
    ],
  },
  {
    code: '510000',
    name: '四川省',
    cities: [
      { code: '510100', name: '成都市' },
      { code: '510700', name: '绵阳市' },
    ],
  },
  {
    code: '440000',
    name: '广东省',
    cities: [
      { code: '440100', name: '广州市' },
      { code: '440300', name: '深圳市' },
    ],
  },
];

/** 按代码优先、名称兜底查省份（兜底兼容存量「名称误存 id」脏数据回填）。 */
const findProvince = (codeOrName: string): RegionNode | undefined =>
  REGION_TREE.find((p) => p.code === codeOrName || p.name === codeOrName);

/** 按代码优先、名称兜底查城市。 */
const findCity = (province: RegionNode, codeOrName: string) =>
  province.cities.find((c) => c.code === codeOrName || c.name === codeOrName);

/** SKU 配置行状态（判别联合；trialSessionCount 未定义=跟随 SKU 当前值，输入即固化）。 */
type SkuConfigRow =
  | {
      skuId: string;
      chargeMode: 'SESSION_PACKAGE';
      /** 商品内课时数（必填 ≥1） */
      lessonCount: number;
      trialSessionCount?: number;
    }
  | {
      skuId: string;
      chargeMode: 'PERIOD';
      /** 期限天数（必填 ≥1；到期日 = 激活日 + periodDays − 1 含当天） */
      periodDays: number;
      /** 卡面标价（分，必填 >0） */
      listPrice: number;
      trialSessionCount?: number;
    };

export interface ProductEditModalProps {
  open: boolean;
  /** 当前编辑的商品（null 为新建；复制时传入源商品并标记 duplicate） */
  editing: ProductItem | null;
  /** 复制模式：按源商品字段新建 DRAFT 副本 */
  duplicate?: boolean;
  /** 可选 SKU 下拉（listSkusForSelector，非 DISABLED） */
  skuOptions: CourseSkuItem[];
  /** 暂存成功后回调（父组件刷新列表） */
  onSaved: () => void;
  /** 上架（先保存后 publish；父组件处理） */
  onPublish: (id: string) => Promise<void>;
  onClose: () => void;
}

export function ProductEditModal({
  open,
  editing,
  duplicate = false,
  skuOptions,
  onSaved,
  onPublish,
  onClose,
}: ProductEditModalProps) {
  const [province, setProvince] = useState('');
  const [city, setCity] = useState('');
  const [name, setName] = useState('');
  const [coverObjectKey, setCoverObjectKey] = useState<string | null>(null);
  const [coverPreviewUrl, setCoverPreviewUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [price, setPrice] = useState<number>(0);
  const [depositPercent, setDepositPercent] = useState<number>(30);
  const [finalDueDays, setFinalDueDays] = useState<number>(7);
  const [features, setFeatures] = useState<string[]>([]);
  const [featureInput, setFeatureInput] = useState('');
  const [configs, setConfigs] = useState<SkuConfigRow[]>([]);
  const [submitting, setSubmitting] = useState(false);
  // 校区归属（issue #7）：null = 机构通用（全校区可见）；选项经共享 hook（ACTIVE + 数据范围过滤）
  const [campusId, setCampusId] = useState<string | null>(null);
  const { campuses: campusList } = useCampusOptions(open);

  const isCopy = duplicate && editing !== null;
  const isEdit = editing !== null && !duplicate;

  // 打开时回填（编辑回填原值；复制回填源商品字段但转 DRAFT 语义）
  useEffect(() => {
    if (!open) return;
    if (editing) {
      // 回填按代码匹配；存量「名称误存 id」脏数据兜底按名称解析为代码
      const provinceNode = findProvince(editing.provinceId) ?? findProvince(editing.provinceName);
      const cityNode = provinceNode
        ? findCity(provinceNode, editing.cityId) ?? findCity(provinceNode, editing.cityName)
        : undefined;
      setProvince(provinceNode?.code ?? '');
      setCity(cityNode?.code ?? '');
      setName(isCopy ? `${editing.name}（副本）` : editing.name);
      setCampusId(editing.campusId ?? null);
      setCoverObjectKey(editing.coverImage ?? null);
      setCoverPreviewUrl(editing.coverImageUrl ?? null);
      setPrice(editing.price / 100);
      setDepositPercent(Math.round(editing.depositRatio * 100));
      setFinalDueDays(editing.finalDueDays);
      setFeatures([...editing.features]);
      setConfigs(
        editing.skuConfigs.map((c) => {
          // 仅回填固化覆盖值；跟随态（null）不携带，保持跟随 SKU
          const trial =
            typeof c.trialSessionCountOverride === 'number'
              ? { trialSessionCount: c.trialSessionCountOverride }
              : {};
          // O-14：chargeMode 下沉后历史数据缺省按课时包回填，不因字段缺失报错
          if (c.chargeMode === 'PERIOD') {
            return {
              skuId: c.skuId,
              chargeMode: 'PERIOD' as const,
              periodDays: c.periodDays ?? 1,
              listPrice: c.listPrice ?? 0,
              ...trial,
            };
          }
          return {
            skuId: c.skuId,
            chargeMode: 'SESSION_PACKAGE' as const,
            lessonCount: c.lessonCount ?? 1,
            ...trial,
          };
        }),
      );
    } else {
      setProvince('');
      setCity('');
      setName('');
      setCoverObjectKey(null);
      setCoverPreviewUrl(null);
      setPrice(0);
      setDepositPercent(30);
      setFinalDueDays(7);
      setFeatures([]);
      setConfigs([]);
      setCampusId(null);
    }
    setFeatureInput('');
  }, [open, editing, isCopy]);

  const skuMap = useMemo(() => {
    const map = new Map<string, CourseSkuItem>();
    skuOptions.forEach((s) => map.set(s.id, s));
    return map;
  }, [skuOptions]);

  /** 期限行卡面标价「一天一次课」估算：SKU 课时标价 ÷ 课时数默认值 × 期限天数（分，四舍五入整数安全）。 */
  const estimatePeriodListPrice = (skuId: string, periodDays: number): number => {
    const sku = skuMap.get(skuId);
    if (!sku || sku.entitlementQuantity <= 0 || periodDays <= 0) return 0;
    return Math.round((sku.listPrice / sku.entitlementQuantity) * periodDays);
  };

  /** 原价合计（Σ行标价：课时行取 SKU 课时标价、期限行取卡面标价）。 */
  const originalTotal = configs.reduce(
    (sum, c) =>
      c.chargeMode === 'PERIOD'
        ? sum + c.listPrice
        : sum + (skuMap.get(c.skuId)?.listPrice ?? 0),
    0,
  );
  const totalLessons = configs
    .filter((c) => c.chargeMode === 'SESSION_PACKAGE')
    .reduce((sum, c) => sum + c.lessonCount, 0);
  const periodCount = configs.filter((c) => c.chargeMode === 'PERIOD').length;
  const bundleSummary =
    periodCount === 0
      ? `${totalLessons} 课时`
      : totalLessons > 0
        ? `${totalLessons} 课时 + ${periodCount} 张期限卡`
        : `${periodCount} 张期限卡`;
  const finalPercent = 100 - depositPercent;

  /** 添加一个 SKU 配置行（默认课时包形态，课时默认取该 SKU entitlementQuantity，design D14）。 */
  const addSkuConfig = () => {
    const available = skuOptions.find((s) => !configs.some((c) => c.skuId === s.id));
    if (!available) {
      message.warning('暂无更多可添加的课程SKU');
      return;
    }
    setConfigs([
      ...configs,
      {
        skuId: available.id,
        chargeMode: 'SESSION_PACKAGE',
        lessonCount: available.entitlementQuantity,
      },
    ]);
  };

  /** 打包行切换售卖形态：按新形态重置该行字段（课时数/期限字段互斥清空，period-pass-admin-ui:SC-03）。 */
  const switchRowMode = (idx: number, mode: SkuChargeMode) => {
    setConfigs((prev) => {
      const next = [...prev];
      const row = next[idx]!;
      const sku = skuMap.get(row.skuId);
      next[idx] =
        mode === 'PERIOD'
          ? {
              skuId: row.skuId,
              chargeMode: 'PERIOD',
              periodDays: 90,
              listPrice: estimatePeriodListPrice(row.skuId, 90),
              trialSessionCount: row.trialSessionCount,
            }
          : {
              skuId: row.skuId,
              chargeMode: 'SESSION_PACKAGE',
              lessonCount: sku?.entitlementQuantity ?? 1,
              trialSessionCount: row.trialSessionCount,
            };
      return next;
    });
  };

  /**
   * 期限天数变化（档位快捷或手改）联动卡面标价估算：
   * 仅当当前标价仍是「未手工修改的估算值」（等于旧天数估算或为 0）时重算，运营已定价不覆盖（SC-02）。
   */
  const changePeriodDays = (idx: number, days: number) => {
    setConfigs((prev) => {
      const next = [...prev];
      const row = next[idx]!;
      if (row.chargeMode !== 'PERIOD') return prev;
      const periodDays = Math.max(1, Math.round(days) || 1);
      const untouched =
        row.listPrice === 0 ||
        row.listPrice === estimatePeriodListPrice(row.skuId, row.periodDays);
      next[idx] = {
        ...row,
        periodDays,
        listPrice: untouched ? estimatePeriodListPrice(row.skuId, periodDays) : row.listPrice,
      };
      return next;
    });
  };

  /** 行内切换 SKU：课时行预填默认课时数；期限行未手工定价则按新 SKU 重算估算。 */
  const changeRowSku = (idx: number, skuId: string) => {
    setConfigs((prev) => {
      const next = [...prev];
      const row = next[idx]!;
      const sku = skuMap.get(skuId);
      if (row.chargeMode === 'SESSION_PACKAGE') {
        next[idx] = { ...row, skuId, lessonCount: sku?.entitlementQuantity ?? row.lessonCount };
      } else {
        const untouched =
          row.listPrice === 0 ||
          row.listPrice === estimatePeriodListPrice(row.skuId, row.periodDays);
        next[idx] = {
          ...row,
          skuId,
          listPrice: untouched ? estimatePeriodListPrice(skuId, row.periodDays) : row.listPrice,
        };
      }
      return next;
    });
  };

  /** 封面上传：凭证签发 -> directUpload 真：cos 直传；假：直接采用 objectKey（design D6）。 */
  const handleCoverUpload = async (file: File) => {
    setUploading(true);
    try {
      const result = await productApi.issueCoverUploadCredentials(file.name);
      await uploadObjectToCos(result, file);
      // objectKey 回填 coverImage；预览经 fake 公有读 URL 或本地 blob（real 上传成功后同 URL 可访问）
      setCoverObjectKey(result.objectKey);
      setCoverPreviewUrl(previewUrlOf(file));
      message.success('封面上传完成');
    } catch (err) {
      message.error(err instanceof Error ? err.message : '封面上传失败');
    } finally {
      setUploading(false);
    }
  };

  /** 校验并组装请求体；不合法时返回 null 并提示。 */
  const buildPayload = (): Payload | null => {
    const provinceNode = findProvince(province);
    const cityNode = provinceNode ? findCity(provinceNode, city) : undefined;
    if (!provinceNode || !cityNode) {
      message.error('请先选择省份和城市');
      return null;
    }
    if (!name.trim()) {
      message.error('请输入商品名称');
      return null;
    }
    if (configs.length === 0) {
      message.error('至少配置一个课程SKU');
      return null;
    }
    const sessionRows = configs.filter((c) => c.chargeMode === 'SESSION_PACKAGE');
    const periodRows = configs.filter((c) => c.chargeMode === 'PERIOD');
    if (sessionRows.some((c) => c.lessonCount < 1)) {
      message.error('课时形态每个SKU的课时数不得小于1');
      return null;
    }
    if (periodRows.some((c) => c.periodDays < 1)) {
      message.error('期限形态每个SKU的期限天数不得小于1');
      return null;
    }
    if (periodRows.some((c) => !c.listPrice || c.listPrice <= 0)) {
      message.error(
        '期限形态每个SKU需填写大于 0 的卡面标价（默认按一天一次课估算预填，参与原价合计与收入分摊）',
      );
      return null;
    }
    if (
      configs.some(
        (c) => c.trialSessionCount !== undefined && (c.trialSessionCount < 1 || c.trialSessionCount > 3),
      )
    ) {
      message.error('试课赠送次数需在 1~3 之间');
      return null;
    }
    if (price < 0) {
      message.error('商品定价不得小于0');
      return null;
    }
    if (depositPercent < 0 || depositPercent > 100) {
      message.error('首付比例需在 0~100% 之间');
      return null;
    }
    if (finalDueDays < 1) {
      message.error('尾款周期至少为 1 天');
      return null;
    }
    // 同一商品内一个 SKU 至多一行（单一形态）；添加入口已过滤重复，此处兜底拦截（period-pass-admin-ui:SC-03）
    const dupSku = new Set(configs.map((c) => c.skuId)).size !== configs.length;
    if (dupSku) {
      message.error('同一商品内一个课程SKU至多一行');
      return null;
    }
    return {
      // 校区归属显式随请求提交（null = 机构通用；编辑语义为整体覆盖）
      campusId,
      provinceId: provinceNode.code,
      provinceName: provinceNode.name,
      cityId: cityNode.code,
      cityName: cityNode.name,
      name: name.trim(),
      coverImage: coverObjectKey ?? undefined,
      price: Math.round(price * 100),
      depositRatio: depositPercent / 100,
      finalDueDays,
      features,
      // 判别联合载荷：课时行 lessonCount，期限行 periodDays + listPrice（服务端互斥 CHECK 兜底）
      skuConfigs: configs.map((c, i) => ({
        skuId: c.skuId,
        chargeMode: c.chargeMode,
        ...(c.chargeMode === 'SESSION_PACKAGE'
          ? { lessonCount: c.lessonCount }
          : { periodDays: c.periodDays, listPrice: c.listPrice }),
        sortOrder: i + 1,
        // 仅固化值随请求提交；未调整省略键=服务端存 NULL 跟随 SKU
        ...(c.trialSessionCount !== undefined ? { trialSessionCount: c.trialSessionCount } : {}),
      })),
    };
  };

  /** 暂存。 */
  const handleSaveDraft = async () => {
    const payload = buildPayload();
    if (!payload) return;
    setSubmitting(true);
    try {
      if (isEdit && editing) {
        await productApi.updateProduct(editing.id, { ...payload, version: editing.version });
      } else {
        await productApi.createProduct(payload);
      }
      message.success('已暂存');
      onSaved();
      onClose();
    } catch (err) {
      message.error(err instanceof Error ? err.message : '保存失败');
    } finally {
      setSubmitting(false);
    }
  };

  /** 上架：二次确认后先保存再 publish。 */
  const handlePublish = async () => {
    const payload = buildPayload();
    if (!payload) return;
    setSubmitting(true);
    try {
      const saved =
        isEdit && editing
          ? await productApi.updateProduct(editing.id, { ...payload, version: editing.version })
          : await productApi.createProduct(payload);
      await onPublish(saved.id);
      onClose();
    } catch (err) {
      message.error(err instanceof Error ? err.message : '上架失败');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DraggableModal
      open={open}
      title={isEdit ? '编辑课程商品' : isCopy ? '复制课程商品' : '新建课程商品'}
      width={920}
      onCancel={onClose}
      destroyOnHidden
      footer={
        <Space>
          <Button onClick={onClose}>取消</Button>
          <Button loading={submitting} onClick={() => void handleSaveDraft()}>
            暂存
          </Button>
          <Popconfirm
            title="确认上架该商品？"
            description="上架后家长端该地区可见并可报名"
            onConfirm={() => handlePublish()}
            okText="确定上架"
            cancelText="取消"
          >
            <Button type="primary" loading={submitting}>
              上架
            </Button>
          </Popconfirm>
        </Space>
      }
    >
      {editing && editing.orderRefCount > 0 && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="该商品已被历史订单引用，历史订单以下单时快照为准，不受本次修改影响"
        />
      )}
      <div style={{ display: 'flex', gap: 24 }}>
        {/* ───── 左栏：配置表单 ───── */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <Space size={12} wrap>
            <div>
              <Text type="secondary">省份</Text>
              <br />
              <Select
                style={{ width: 140 }}
                value={province || undefined}
                onChange={(v) => {
                  setProvince(v);
                  setCity('');
                  setConfigs([]);
                }}
                options={REGION_TREE.map((p) => ({ label: p.name, value: p.code }))}
                placeholder="选择省份"
                data-testid="region-province-select"
              />
            </div>
            <div>
              <Text type="secondary">城市</Text>
              <br />
              <Select
                style={{ width: 140 }}
                value={city || undefined}
                onChange={setCity}
                options={(findProvince(province)?.cities ?? []).map((c) => ({ label: c.name, value: c.code }))}
                placeholder={province ? '选择城市' : '请先选择省份'}
                disabled={!province}
                data-testid="region-city-select"
              />
            </div>
          </Space>
          <Divider style={{ margin: '12px 0' }} />
          <Text type="secondary">校区归属（选填）</Text>
          <div style={{ marginBottom: 12, marginTop: 4 }}>
            <Select
              style={{ width: 220 }}
              allowClear
              value={campusId ?? undefined}
              onChange={(v) => setCampusId(v ?? null)}
              options={campusList.map((c) => ({ label: c.name, value: c.id }))}
              placeholder="机构通用（全校区可见）"
              data-testid="product-campus-select"
            />
          </div>
          <Text type="secondary">商品名称</Text>
          <Input
            style={{ marginBottom: 12, marginTop: 4 }}
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={!city}
            placeholder={!city ? '请先选择城市' : '请输入商品名称'}
            maxLength={128}
          />
          <Text type="secondary">封面图片（jpg/jpeg/png/webp）</Text>
          <div style={{ marginBottom: 12, marginTop: 4 }}>
            {coverPreviewUrl && (
              <div style={{ marginBottom: 8, position: 'relative', width: 200 }}>
                <img
                  src={coverPreviewUrl}
                  alt="封面预览"
                  style={{ width: 200, height: 120, objectFit: 'cover', borderRadius: 8 }}
                />
                <Button
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  style={{ position: 'absolute', top: 4, right: 4 }}
                  onClick={() => {
                    setCoverObjectKey(null);
                    setCoverPreviewUrl(null);
                  }}
                />
              </div>
            )}
            <Upload
              accept=".jpg,.jpeg,.png,.webp"
              showUploadList={false}
              customRequest={(options) => {
                void handleCoverUpload(options.file as File);
                options.onSuccess?.({});
              }}
            >
              <Button icon={<UploadOutlined />} loading={uploading}>
                {coverPreviewUrl ? '重新上传' : '上传封面'}
              </Button>
            </Upload>
          </div>
          <Divider style={{ margin: '12px 0' }} />
          <Text type="secondary">SKU 打包配置</Text>
          {!city && (
            <div style={{ fontSize: 12, color: '#faad14', marginTop: 2 }}>请先设置城市</div>
          )}
          <div style={{ marginTop: 4 }}>
            {configs.map((c, idx) => {
              return (
                <Space key={c.skuId} style={{ display: 'flex', marginBottom: 8 }} align="center" wrap>
                  <Select
                    style={{ width: 180 }}
                    value={c.skuId}
                    disabled={!city}
                    options={skuOptions
                      .filter((s) => s.id === c.skuId || !configs.some((x) => x.skuId === s.id))
                      .map((s) => ({ label: `${s.name}（¥${(s.listPrice / 100).toFixed(2)}）`, value: s.id }))}
                    onChange={(v) => changeRowSku(idx, v)}
                  />
                  <Select
                    style={{ width: 96 }}
                    value={c.chargeMode}
                    onChange={(mode) => switchRowMode(idx, mode)}
                    options={toSelectOptions(chargeModeLabel)}
                    aria-label={`配置${idx + 1}售卖形态`}
                    data-testid={`config-mode-${idx + 1}`}
                  />
                  {c.chargeMode === 'SESSION_PACKAGE' ? (
                    <InputNumber
                      style={{ width: 110 }}
                      min={1}
                      value={c.lessonCount}
                      suffix="课时"
                      aria-label={`配置${idx + 1}课时数`}
                      onChange={(v) => {
                        const next = [...configs];
                        const row = next[idx]!;
                        if (row.chargeMode !== 'SESSION_PACKAGE') return;
                        next[idx] = { ...row, lessonCount: v ?? 1 };
                        setConfigs(next);
                      }}
                    />
                  ) : (
                    <>
                      <Select
                        style={{ width: 92 }}
                        placeholder="自定义"
                        value={
                          PERIOD_PRESET_OPTIONS.find((p) => p.days === c.periodDays)?.days ??
                          undefined
                        }
                        onChange={(days) => changePeriodDays(idx, days)}
                        options={PERIOD_PRESET_OPTIONS.map((p) => ({ label: p.label, value: p.days }))}
                        aria-label={`配置${idx + 1}期限档位`}
                        data-testid={`config-preset-${idx + 1}`}
                      />
                      <InputNumber
                        style={{ width: 100 }}
                        min={1}
                        value={c.periodDays}
                        suffix="天"
                        aria-label={`配置${idx + 1}期限天数`}
                        onChange={(v) => changePeriodDays(idx, v ?? 1)}
                      />
                      <InputNumber
                        style={{ width: 130 }}
                        min={0}
                        value={c.listPrice / 100}
                        suffix="元标价"
                        placeholder={`${(estimatePeriodListPrice(c.skuId, c.periodDays) / 100).toFixed(0)}`}
                        aria-label={`配置${idx + 1}卡面标价`}
                        onChange={(v) => {
                          const next = [...configs];
                          const row = next[idx]!;
                          if (row.chargeMode !== 'PERIOD') return;
                          // 元转分整数安全（Math.round 抹平浮点误差）
                          next[idx] = { ...row, listPrice: Math.round((v ?? 0) * 100) };
                          setConfigs(next);
                        }}
                      />
                    </>
                  )}
                  <InputNumber
                    style={{ width: 120 }}
                    min={1}
                    max={3}
                    precision={0}
                    value={c.trialSessionCount ?? skuMap.get(c.skuId)?.trialSessionCount}
                    suffix="试课"
                    aria-label={`配置${idx + 1}试课次数`}
                    onChange={(v) => {
                      const next = [...configs];
                      const row = next[idx]!;
                      // 清空（null/undefined）= 回退跟随 SKU 当前值；输入即固化覆盖
                      next[idx] =
                        v === null || v === undefined
                          ? (
                              row.chargeMode === 'SESSION_PACKAGE'
                                ? { skuId: row.skuId, chargeMode: row.chargeMode, lessonCount: row.lessonCount }
                                : {
                                    skuId: row.skuId,
                                    chargeMode: row.chargeMode,
                                    periodDays: row.periodDays,
                                    listPrice: row.listPrice,
                                  }
                            ) as SkuConfigRow
                          : ({ ...row, trialSessionCount: v } as SkuConfigRow);
                      setConfigs(next);
                    }}
                  />
                  {c.trialSessionCount !== undefined && <Tag color="orange">已单独设置</Tag>}
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    aria-label={`删除配置${idx + 1}`}
                    onClick={() => setConfigs(configs.filter((_, i) => i !== idx))}
                  />
                </Space>
              );
            })}
            <Button
              size="small"
              icon={<PlusOutlined />}
              disabled={!city}
              onClick={addSkuConfig}
              style={{ marginTop: 4 }}
            >
              添加课程SKU
            </Button>
          </div>
<Divider style={{ margin: '12px 0' }} />
          <Space size={12} wrap align="start">
            <div>
              <Text type="secondary">商品定价（元）</Text>
              <br />
              <InputNumber
                style={{ width: 140 }}
                min={0}
                precision={2}
                value={price}
                onChange={(v) => setPrice(v ?? 0)}
              />
            </div>
            <div>
              <Text type="secondary">首付款比例（%）</Text>
              <br />
              <InputNumber
                style={{ width: 120 }}
                min={0}
                max={100}
                precision={0}
                value={depositPercent}
                onChange={(v) => setDepositPercent(v ?? 0)}
              />
            </div>
            <div>
              <Text type="secondary">尾款比例（自动）</Text>
              <br />
              <InputNumber style={{ width: 120 }} value={finalPercent} disabled suffix="%" />
            </div>
            <div>
              <Text type="secondary">尾款周期（天）</Text>
              <br />
              <InputNumber
                style={{ width: 120 }}
                min={1}
                value={finalDueDays}
                onChange={(v) => setFinalDueDays(v ?? 7)}
              />
            </div>
          </Space>
          <Divider style={{ margin: '12px 0' }} />
          <Space size={12} wrap align="start">
            <div>
              <Text type="secondary">原价合计（系统计算）</Text>
              <br />
              <Text strong>¥{(originalTotal / 100).toFixed(2)}</Text>
            </div>
            <div>
              <Text type="secondary">课时/期限</Text>
              <br />
              <Text strong>{bundleSummary}</Text>
            </div>
          </Space>
          <Divider style={{ margin: '12px 0' }} />
          <Text type="secondary">卖点标签（三端同步展示）</Text>
          <div style={{ marginBottom: 12, marginTop: 4 }}>
            {features.map((f) => (
              <Tag
                key={f}
                closable
                onClose={() => setFeatures(features.filter((x) => x !== f))}
                style={{ marginBottom: 4 }}
              >
                {f}
              </Tag>
            ))}
            <Space.Compact>
              <Input
                size="small"
                style={{ width: 300 }}
                placeholder="输入后按回车保存，每个标签一次"
                maxLength={32}
                value={featureInput}
                onChange={(e) => setFeatureInput(e.target.value)}
                onPressEnter={() => {
                  const v = featureInput.trim();
                  if (v && !features.includes(v)) setFeatures([...features, v]);
                  setFeatureInput('');
                }}
              />
              <Button
                size="small"
                onClick={() => {
                  const v = featureInput.trim();
                  if (v && !features.includes(v)) setFeatures([...features, v]);
                  setFeatureInput('');
                }}
              >
                添加
              </Button>
            </Space.Compact>
          </div>
          
        </div>

        {/* ───── 右栏：家长端实时预览 ───── */}
        <div style={{ width: 280, flexShrink: 0 }}>
          <Text type="secondary">家长端实时预览</Text>
          <div
            style={{
              border: '1px solid #eee',
              borderRadius: 12,
              padding: 12,
              marginTop: 4,
              background: '#fafafa',
            }}
          >
            {coverPreviewUrl ? (
              <img
                src={coverPreviewUrl}
                alt="封面预览"
                style={{ width: '100%', height: 120, objectFit: 'cover', borderRadius: 8, marginBottom: 12 }}
              />
            ) : (
              <div
                style={{
                  width: '100%',
                  height: 120,
                  background: '#e3f2ef',
                  borderRadius: 8,
                  marginBottom: 12,
                }}
              />
            )}
            <Title level={5} style={{ marginTop: 0 }} ellipsis>
              {name || '商品名称预览'}
            </Title>
            <div style={{ marginBottom: 8 }}>
              {features.map((f) => (
                <Tag key={f} color="orange">
                  {f}
                </Tag>
              ))}
            </div>
            {configs.map((c) => {
              const sku = skuMap.get(c.skuId);
              return (
                <div key={c.skuId} style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>
                  {sku?.name ?? 'SKU'} ·{' '}
                  {c.chargeMode === 'PERIOD'
                    ? `期限${c.periodDays}天（有效期内不限次上课）`
                    : `${c.lessonCount}课时`}
                  {' · 赠试课 '}
                  {c.trialSessionCount ?? sku?.trialSessionCount ?? '-'} 次
                </div>
              );
            })}
            <div style={{ marginTop: 8, fontSize: 12, color: '#999' }}>{bundleSummary}</div>
            <div style={{ marginTop: 8, display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: '#999', textDecoration: 'line-through', fontSize: 12 }}>
                ¥{(originalTotal / 100).toFixed(2)}
              </span>
              <span style={{ fontSize: 20, color: '#fa8c16', fontWeight: 700 }}>
                ¥{(price ?? 0).toFixed(2)}
              </span>
            </div>
            <div style={{ marginTop: 4, fontSize: 12, color: '#999' }}>
              首付款比例 {depositPercent}%，首付结清后 {finalDueDays} 天内交齐尾款
            </div>
          </div>
        </div>
      </div>
    </DraggableModal>
  );
}

/** 请求体类型（buildPayload 返回；SKU 配置为售卖形态判别联合）。 */
interface Payload {
  /** 校区归属（null = 机构通用，全校区可见） */
  campusId: string | null;
  provinceId: string;
  provinceName: string;
  cityId: string;
  cityName: string;
  name: string;
  coverImage?: string;
  price: number;
  depositRatio: number;
  finalDueDays: number;
  features: string[];
  skuConfigs: Array<{
    skuId: string;
    chargeMode: SkuChargeMode;
    lessonCount?: number;
    periodDays?: number;
    listPrice?: number;
    sortOrder: number;
    trialSessionCount?: number;
  }>;
}

/** fake 模式预览 URL：凭证响应无访问 URL 时退回本地 blob（保证预览可见）。 */
function previewUrlOf(file: File): string {
  return URL.createObjectURL(file);
}
